import isNil from 'lodash-es/isNil'
import React, { useEffect, useRef, useState } from 'react'

import {
  Button,
  ButtonClean,
  ButtonGroup,
  Checkbox,
  Input,
  NumberString,
  Popper,
  Tabs,
  useMediaQueryContext,
} from '@fourthwall/components'
import { isIntegerString, isPositiveIntegerString } from '@fourthwall/utils/dist/string'

import * as S from './styled'
import type { StockTooltipProps } from './types'

const UNLIMITED_VALUE = 'Unlimited'

export const StockTooltip = ({ quantity, disabled = false, onChange }: StockTooltipProps) => {
  const media = useMediaQueryContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const initialQuantityValue = !isNil(quantity) ? quantity.toString() : UNLIMITED_VALUE
  const [newQuantity, setNewQuantity] = useState(initialQuantityValue)
  const [adjustBy, setAdjustBy] = useState('0')
  const [isOpen, setOpen] = useState(false)

  const isUnlimited = newQuantity === UNLIMITED_VALUE
  const isNewQuantityInvalid = !isUnlimited && !isPositiveIntegerString(newQuantity)
  const isAdjustByInvalid =
    !isUnlimited && (!isIntegerString(adjustBy) || parseInt(newQuantity) < 0)
  const isDisabled = isNewQuantityInvalid || isAdjustByInvalid

  const setAdjustedQuantity = (adjustValue: any) => {
    const adjustParsed = isIntegerString(adjustValue) ? parseInt(adjustValue) : 0
    const initialQuantity = quantity || 0

    setNewQuantity((initialQuantity + adjustParsed).toString())
  }

  const setInputFocus = () => {
    if (!media.tablet) return

    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    })
  }

  useEffect(() => {
    setNewQuantity(initialQuantityValue)
  }, [quantity])

  useEffect(() => {
    if (!isOpen && !isUnlimited) {
      setAdjustBy('0')
      setAdjustedQuantity('0')
    } else if (!isUnlimited) {
      setInputFocus()
    }
  }, [isOpen])

  const handleAdjustByChange = (adjustByValue: any) => {
    setAdjustBy(adjustByValue)
    setAdjustedQuantity(adjustByValue)
  }

  const handleUnlimitedCheckboxChange = () => {
    if (!isUnlimited) {
      setAdjustBy('0')
      setNewQuantity(UNLIMITED_VALUE)
    } else {
      setAdjustedQuantity(adjustBy)
    }
  }

  const handleTabSelect = () => {
    if (!isUnlimited) {
      setAdjustedQuantity(adjustBy)
      setInputFocus()
    }
  }

  const renderQuantityValue = (value: string) => {
    return isIntegerString(value) ? <NumberString value={parseInt(value)} /> : value
  }

  const handleSubmit = () => {
    if (!isDisabled) {
      const resultQuantity = isUnlimited ? undefined : parseInt(newQuantity)

      onChange(quantity, resultQuantity)
    }

    setOpen(false)
  }

  const renderTooltipContent = () => (
    <S.TooltipContainer>
      <Tabs defaultIndex={!quantity ? 1 : 0} onSelect={handleTabSelect}>
        <S.TabsList>
          <Tabs.Item
            name="Adjust quantity"
            style={{ paddingTop: 16, paddingBottom: 16 }}
            variant="dimmed"
          />
          <Tabs.Item
            name="Set new quantity"
            style={{ paddingTop: 16, paddingBottom: 16 }}
            variant="dimmed"
          />
        </S.TabsList>
        <S.TabsPanelContainer>
          <Tabs.Panel style={{ padding: 16 }}>
            <S.PanelContainer>
              <S.PanelColumn>
                <Input
                  label="Adjust by"
                  value={adjustBy}
                  type="number"
                  invalid={isAdjustByInvalid}
                  disabled={isUnlimited}
                  placeholder="0"
                  inputRef={inputRef}
                  onValueChange={handleAdjustByChange}
                />
              </S.PanelColumn>
              <S.PanelColumn>
                <S.ColumnLabel>New quantity</S.ColumnLabel>
                <S.ColumnValue>{renderQuantityValue(newQuantity)}</S.ColumnValue>
              </S.PanelColumn>
            </S.PanelContainer>
          </Tabs.Panel>
          <Tabs.Panel style={{ padding: 16 }}>
            <S.PanelContainer>
              <S.PanelColumn>
                <S.ColumnLabel>Current quantity</S.ColumnLabel>
                <S.ColumnValue>{renderQuantityValue(initialQuantityValue)}</S.ColumnValue>
              </S.PanelColumn>
              <S.PanelColumn>
                <Input
                  label="New quantity"
                  value={newQuantity}
                  type={isUnlimited ? 'text' : 'number'}
                  hideSpinner
                  disabled={isUnlimited}
                  invalid={isNewQuantityInvalid}
                  inputRef={inputRef}
                  onValueChange={setNewQuantity}
                />
              </S.PanelColumn>
            </S.PanelContainer>
          </Tabs.Panel>
        </S.TabsPanelContainer>
      </Tabs>
      <S.UnlimitedCheckboxContainer>
        <Checkbox
          label="Make unlimited"
          value={isUnlimited}
          onValueChange={handleUnlimitedCheckboxChange}
        />
      </S.UnlimitedCheckboxContainer>
      <S.TooltipFooter>
        <ButtonGroup space="medium" fullWidth direction={media.tablet ? 'row' : 'column-reverse'}>
          <Button label="Cancel" onClick={() => setOpen(false)} fullWidth />
          <Button
            label="Save"
            appearance="primary"
            disabled={isDisabled}
            onClick={handleSubmit}
            fullWidth
          />
        </ButtonGroup>
      </S.TooltipFooter>
    </S.TooltipContainer>
  )

  return (
    <Popper
      isOpen={isOpen}
      referenceComponent={
        <ButtonClean
          label={renderQuantityValue(initialQuantityValue)}
          disabled={disabled}
          onClick={() => setOpen(true)}
        />
      }
      popperProps={{
        placement: 'bottom-start',
      }}
      style={{ paddingTop: media.tablet ? 8 : 0 }}
      onClose={() => setOpen(false)}
    >
      {renderTooltipContent()}
    </Popper>
  )
}
